/** TakeLead Web — App-Container (Design Tokens, Auth, Router, Query, Toaster, Analytics-Consent). */
import { useState, useEffect, useCallback, type ReactNode } from "react";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { AnalyticsConsentBanner } from "@/components/AnalyticsConsentBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { getStoredAnalyticsConsent, setStoredAnalyticsConsent, type AnalyticsConsentStatus } from "@/lib/analytics-consent";
import { applyPostHogConsent, isPostHogAvailable } from "@/lib/posthog";
import { useAuth } from "@/contexts/useAuth";
import { useProfile } from "@/hooks/useAdmin";
import { apiPatch } from "@/api";
import i18n from "@/i18n";
import { router } from "@/router";
import "@/index.css";

const LOADING_FALLBACK = (
  <div className="flex min-h-screen items-center justify-center bg-bg text-sm text-text-muted">
    Wird geladen…
  </div>
);

/** Prüft bereit: init abgeschlossen und store gesetzt (useTranslation braucht i18n.store in subscribe()). */
function isI18nUsable(instance: typeof i18n | null | undefined): boolean {
  if (instance == null) return false;
  return instance.isInitialized === true && instance.store != null;
}

/**
 * Safety net: rendert Kinder erst wenn i18n bereit ist.
 * Da main.tsx initI18n() awaited bevor App rendert, ist i18n normalerweise sofort ready.
 * Falls nicht (HMR, edge case), wartet Gate auf das "initialized" Event.
 */
function I18nGate({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const [ready, setReady] = useState(() => isI18nUsable(i18n));
  useEffect(() => {
    if (ready) return;
    const onInited = () => {
      if (isI18nUsable(i18n)) setReady(true);
    };
    i18n?.on?.("initialized", onInited);
    return () => i18n?.off?.("initialized", onInited);
  }, [ready]);
  if (!ready) {
    return <>{fallback ?? LOADING_FALLBACK}</>;
  }
  return <>{children}</>;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function AnalyticsConsentGate() {
  const { state } = useAuth();
  const { data: profile } = useProfile();
  const token = state.status === "authenticated" ? state.token : null;
  const [consent, setConsent] = useState<AnalyticsConsentStatus>(() => getStoredAnalyticsConsent());
  const showBanner = consent === null && isPostHogAvailable();

  /** PostHog-Start nur über PostHogPageView (+ applyPostHogConsent) — vermeidet doppeltes init parallel zum Router-Commit. */

  useEffect(() => {
    const next = profile?.analytics_consent_accepted === true ? "accepted"
      : profile?.analytics_consent_accepted === false ? "rejected"
      : null;
    if (next == null || next === consent) return;
    setStoredAnalyticsConsent(next);
    setConsent(next);
    applyPostHogConsent(next);
  }, [profile?.analytics_consent_accepted, consent]);

  const handleConsentChange = useCallback(
    (status: AnalyticsConsentStatus) => {
      setConsent(status);
      if (token && (status === "accepted" || status === "rejected")) {
        apiPatch("/me", token, { analytics_consent_accepted: status === "accepted" }).then(() => {
          // best-effort; profile invalidation optional
        });
      }
    },
    [token]
  );

  if (!showBanner) return null;
  return <AnalyticsConsentBanner onConsentChange={handleConsentChange} />;
}

export function App() {
  return (
    <I18nGate fallback={LOADING_FALLBACK}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ErrorBoundary>
              <RouterProvider router={router} />
              <AnalyticsConsentGate />
            </ErrorBoundary>
            <ErrorBoundary fallback={<></>}>
              <Toaster position="top-right" />
            </ErrorBoundary>
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </I18nGate>
  );
}
